import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Empty, Loader } from "@zfp-akademie-a-s/zfp-design";
import { FormSelect } from "@zfp-akademie-a-s/zfp-design/form";
import { ErrorIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { ProposedSolutionDeleteDialog } from "Components/Analyses/GoalsFulfillment/ProposedSolutionDeleteDialog";
import { invalidateProposalQueries } from "Components/Analyses/GoalsFulfillment/invalidateProposalQueries";
import {
  type ProposedSolutionWrapperFormType,
  getProposedSolutionWrapperFormSchema,
} from "Forms/Schemas/GoalsFulfillment/ProposedSolution/proposedSolutionWrapperFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import { useDeleteContractProposal, useGetContractProposal, useListAnalyzerPartners } from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  ContractProposalBaseInvestmentToolType,
  type ContractProposalBaseSolutionHorizonType,
  type ListAnalyzerPartnersToolType,
} from "Infrastructure/ApiZfp/Schema";
import type { ContractProposalDetailCommodity } from "Infrastructure/ApiZfp/Schema/contractProposalDetailCommodity";
import type { ContractProposalDetailInvestment } from "Infrastructure/ApiZfp/Schema/contractProposalDetailInvestment";
import type { ContractProposalDetailOther } from "Infrastructure/ApiZfp/Schema/contractProposalDetailOther";
import type { ContractProposalDetailPensionSaving } from "Infrastructure/ApiZfp/Schema/contractProposalDetailPensionSaving";
import type { ContractProposalDetailSaving } from "Infrastructure/ApiZfp/Schema/contractProposalDetailSaving";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  forwardRef,
  startTransition,
  useContext,
  useDeferredValue,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { CommoditiesForm } from "./CommoditiesForm";
import { InvestmentsForm } from "./InvestmentsForm";
import { OtherForm } from "./OtherForm";
import { PensionForm } from "./PensionForm";
import { SavingsForm } from "./SavingsForm";

type Props = {
  readonly edit?: boolean;
  readonly proposedSolutionID?: string;
  readonly horizonType: ContractProposalBaseSolutionHorizonType;
  readonly onSuccess?: () => void;
  readonly onFormTypeChange?: (type: ContractProposalBaseInvestmentToolType) => void;
  readonly initialFormType?: ContractProposalBaseInvestmentToolType;
};

export type ProposedSolutionFormHandle = {
  readonly submitForm: () => void;
  readonly triggerValidation: () => void;
  readonly deleteSolution?: () => void;
};

export const ProposedSolutionFormWrapper = forwardRef<ProposedSolutionFormHandle, Props>(
  ({ edit, horizonType, proposedSolutionID, onSuccess, onFormTypeChange, initialFormType }, ref) => {
    const { t } = useResource();
    const { analysisId, versionId } = useContext(AnalysisDetailContext);
    const queryClient = useQueryClient();
    const innerFormRef = useRef<ProposedSolutionFormHandle>(null);

    const { control, watch, setValue, clearErrors, handleSubmit } = useForm<ProposedSolutionWrapperFormType>({
      resolver: yupResolver(getProposedSolutionWrapperFormSchema(t)),
      mode: "onChange",
      defaultValues: {
        formType: initialFormType ?? ContractProposalBaseInvestmentToolType.investment,
        partner: "",
      },
    });

    const formType = watch("formType");
    const deferredFormType = useDeferredValue(formType);
    const partner = watch("partner");

    const showPartners =
      formType !== ContractProposalBaseInvestmentToolType.other && formType !== ContractProposalBaseInvestmentToolType.saving;

    const { data: partnersData } = useListAnalyzerPartners({ toolType: formType as ListAnalyzerPartnersToolType });

    const partnerOptions: Option[] = useMemo(
      () => partnersData?.map((partner) => ({ value: `${partner.id}`, name: partner.name ?? "" })) ?? [],
      [partnersData],
    );

    const { data: solutionData, isError: isSolutionError } = useGetContractProposal(analysisId, versionId, proposedSolutionID || "", {
      query: { enabled: !!proposedSolutionID },
    });

    useEffect(() => {
      if (initialFormType != null) {
        setValue("formType", initialFormType);
      }
    }, [initialFormType, setValue]);

    useEffect(() => {
      if (solutionData?.partner?.id != null && partnerOptions.length > 0) {
        setValue("partner", `${solutionData.partner.id}`);
      }
    }, [solutionData, partnerOptions, setValue]);

    const formTypeOptions: Option[] = useMemo(
      () =>
        (Object.keys(ContractProposalBaseInvestmentToolType) as Array<keyof typeof ContractProposalBaseInvestmentToolType>).map(
          (value) => ({
            value,
            name: t(Resources.AnalysisDetail.GoalsFulfillment.InvestmentToolTypeOptions[value]),
          }),
        ),
      [t],
    );

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const { mutate: deleteMutation, isPending: isDeletePending } = useDeleteContractProposal({
      mutation: {
        onSuccess: () => invalidateProposalQueries(queryClient, analysisId, versionId),
        onError: () => toast.error(t(Resources.AnalysisDetail.Toast.ActionError)),
      },
    });

    const deleteSolution = () => {
      if (!edit || !proposedSolutionID) return;
      deleteMutation(
        { analysisId, versionId, contractProposalGuid: proposedSolutionID },
        {
          onSuccess: () => {
            setIsDeleteDialogOpen(false);
            onSuccess?.();
          },
          onError: () => setIsDeleteDialogOpen(false),
        },
      );
    };

    useImperativeHandle(ref, () => ({
      triggerValidation: () => {},
      submitForm: () => {
        innerFormRef.current?.triggerValidation();

        handleSubmit(() => {
          innerFormRef.current?.submitForm();
        })();
      },
      deleteSolution: () => setIsDeleteDialogOpen(true),
    }));
    if (edit && !solutionData) {
      return isSolutionError ? (
        <Empty
          title={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Title)}
          text={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Text)}
          iconSize={40}
          icon={<ErrorIcon sx={{ color: "grey.400" }} />}
        />
      ) : (
        <Loader />
      );
    }

    return (
      <>
        <form>
          <Stack spacing={Spacing.XXL} mb={Spacing.XXL}>
            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={4}>
                  <FormSelect
                    control={control}
                    name="formType"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.InvestmentToolTypeOptions.Title)}
                    required
                    options={toSelectOptions(formTypeOptions)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    disabled={edit}
                    onChange={(value) => {
                      startTransition(() => {
                        setValue("partner", "");
                        clearErrors("partner");
                        onFormTypeChange?.(value as ContractProposalBaseInvestmentToolType);
                      });
                    }}
                    fullWidth
                  />
                </Grid>
                {showPartners && (
                  <Grid size={8}>
                    <FormSelect
                      control={control}
                      name="partner"
                      label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Partner)}
                      required
                      options={toSelectOptions(partnerOptions)}
                      placeholder={t(Resources.Common.SelectPlaceholder)}
                      fullWidth
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Stack>
        </form>

        {deferredFormType === ContractProposalBaseInvestmentToolType.investment && (
          <InvestmentsForm
            partner={partner}
            ref={innerFormRef}
            edit={edit}
            initialData={solutionData as ContractProposalDetailInvestment}
            proposedSolutionId={proposedSolutionID}
            horizonType={horizonType}
            onSuccess={onSuccess}
          />
        )}
        {deferredFormType === ContractProposalBaseInvestmentToolType.commodity && (
          <CommoditiesForm
            partner={partner}
            ref={innerFormRef}
            edit={edit}
            initialData={solutionData as ContractProposalDetailCommodity}
            proposedSolutionId={proposedSolutionID}
            horizonType={horizonType}
            onSuccess={onSuccess}
          />
        )}
        {deferredFormType === ContractProposalBaseInvestmentToolType.pension_saving && (
          <PensionForm
            partner={partner}
            ref={innerFormRef}
            edit={edit}
            initialData={solutionData as ContractProposalDetailPensionSaving}
            proposedSolutionId={proposedSolutionID}
            horizonType={horizonType}
            onSuccess={onSuccess}
          />
        )}
        {deferredFormType === ContractProposalBaseInvestmentToolType.saving && (
          <SavingsForm
            partner={partner}
            ref={innerFormRef}
            edit={edit}
            initialData={solutionData as ContractProposalDetailSaving}
            proposedSolutionId={proposedSolutionID}
            horizonType={horizonType}
            onSuccess={onSuccess}
          />
        )}
        {deferredFormType === ContractProposalBaseInvestmentToolType.other && (
          <OtherForm
            partner={partner}
            ref={innerFormRef}
            edit={edit}
            initialData={solutionData as ContractProposalDetailOther}
            proposedSolutionId={proposedSolutionID}
            horizonType={horizonType}
            onSuccess={onSuccess}
          />
        )}

        <ProposedSolutionDeleteDialog
          isOpen={isDeleteDialogOpen}
          isPending={isDeletePending}
          onClose={() => !isDeletePending && setIsDeleteDialogOpen(false)}
          onConfirm={deleteSolution}
        />
      </>
    );
  },
);
